import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiExternalLink, FiCheck, FiX } from "react-icons/fi";
import {
  userService,
  FullUserProfile,
} from "../services/userService";
import { integrationsService, UserIntegration } from "../services/integrationsService";
import { authService } from "../services/authService";
import { PageHeader, ToastContainer, useToast } from "../components/common";
import { getIntegrationIconSrc } from "../utils/integrationIcons";
import UpgradeModal from "../components/common/UpgradeModal";
import PaymentSuccessModal from "../components/common/PaymentSuccessModal";
import { useSubscription } from "../hooks/useSubscription";
import { usePaymentSuccess } from "../hooks/usePaymentSuccess";
import { createBillingPortalSession, getPromoStatus, type PromoStatusResult } from "../services/paymentsService";

// Styles
import "../styles/profile.css";

// Type-safe icon wrappers
const ExternalLinkIcon = FiExternalLink as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const XIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

// Plan display names
const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: "Free",
  basic: "Basic",
  pro: "Pro",
  enterprise: "Enterprise",
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, success, error: showError } = useToast();
  const { features, loading: subscriptionLoading } = useSubscription();
  const { showModal: showPaymentSuccessModal, planTier: paymentPlanTier, closeModal: closePaymentSuccessModal } = usePaymentSuccess();

  // State
  const [profile, setProfile] = useState<FullUserProfile | null>(null);
  const [integrations, setIntegrations] = useState<UserIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [promoStatus, setPromoStatus] = useState<PromoStatusResult | null>(null);

  // Form states
  const [profileForm, setProfileForm] = useState({
    full_name: "",
  });

  const [companyForm, setCompanyForm] = useState({
    name: "",
    description: "",
    website_url: "",
  });

  useEffect(() => {
    loadProfile();
    loadPromoStatus();
  }, []);

  const loadPromoStatus = async () => {
    try {
      const status = await getPromoStatus();
      setPromoStatus(status);
    } catch (err) {
      // Silent fail — promo status is supplementary info
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await userService.getFullProfile();

      if (!data || !data.profile) {
        throw new Error("Profile data is empty");
      }

      setProfile(data);

      setProfileForm({
        full_name: data.profile.full_name || "",
      });

      setCompanyForm({
        name:
          data.profile.onboarding?.companyName || data.company?.name || "",
        description: data.company?.description || "",
        website_url:
          data.profile.onboarding?.companyWebsite ||
          data.company?.website_url ||
          "",
      });

      // Load integrations via integrationsService (uses backend API)
      try {
        const userIntegrations = await integrationsService.getUserIntegrations();
        setIntegrations(userIntegrations);
      } catch (intErr) {
        console.error("Failed to load integrations:", intErr);
        // Don't fail the whole profile load if integrations fail
      }

    } catch (err) {
      console.error("Profile load error:", err);
      setError(err instanceof Error ? err.message : "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log("Saving profile with data:", { profileForm, companyForm });

      // Update profile (full_name only - email is read-only)
      try {
        await userService.updateProfile(profileForm);
        console.log("Profile updated successfully");
      } catch (profileErr) {
        console.error("Profile update error:", profileErr);
        throw profileErr;
      }

      // Update company info
      try {
        console.log("Updating company with:", companyForm);
        await userService.updateCompany(companyForm);
        console.log("Company updated successfully");
      } catch (companyErr) {
        console.error("Company update error:", companyErr);
        throw companyErr;
      }

      setIsEditing(false);
      await loadProfile(); // Refresh data
      success("Profile Saved", "Your changes have been saved successfully.");
    } catch (err) {
      console.error("Save error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to save profile";
      setError(errorMessage);
      showError("Save Failed", errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset forms to current profile data
    if (profile) {
      setProfileForm({
        full_name: profile.profile.full_name || "",
      });
      setCompanyForm({
        name:
          profile.profile.onboarding?.companyName || profile.company?.name || "",
        description: profile.company?.description || "",
        website_url:
          profile.profile.onboarding?.companyWebsite ||
          profile.company?.website_url ||
          "",
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate("/login");
    } catch (err) {
      showError("Logout Failed", "Failed to sign out. Please try again.");
    }
  };

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true);
      const { url } = await createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to open billing portal";
      showError("Billing Portal Error", message);
      setManagingSubscription(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="su-profile-page">
        <div className="su-profile-loading">Loading profile...</div>
      </div>
    );
  }

  // Error state (no profile)
  if (error && !profile) {
    return (
      <div className="su-profile-page">
        <div className="su-profile-error">
          <div className="su-profile-error-text">Error: {error}</div>
          <button onClick={loadProfile} className="su-btn-danger">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="su-profile-page">
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Page Header */}
      <PageHeader
        title="Profile"
        subtitle="Manage your personal and company information"
        action={
          <div className="su-profile-header-actions">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="su-btn-secondary"
                  disabled={saving}
                >
                  CANCEL
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="su-btn-primary"
                  disabled={saving}
                >
                  {saving ? "SAVING..." : "SAVE CHANGES"}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="su-btn-primary"
              >
                EDIT PROFILE
              </button>
            )}
          </div>
        }
      />

      {/* Error Banner */}
      {error && (
        <div className="su-profile-error">
          <div className="su-profile-error-text">{error}</div>
        </div>
      )}

      {/* Personal Information Section */}
      <div className="su-section">
        <div className="su-section-header">
          <h2 className="su-text-section-heading">Personal Information</h2>
        </div>
        <div className="su-profile-form-grid">
          <div className="su-profile-form-group">
            <label className="su-profile-label">Full Name</label>
            {isEditing ? (
              <input
                type="text"
                value={profileForm.full_name}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, full_name: e.target.value })
                }
                className="su-profile-input"
                placeholder="Enter your full name"
              />
            ) : (
              <div className="su-profile-value">
                {profile?.profile.full_name || (
                  <span className="su-profile-value-muted">Not specified</span>
                )}
              </div>
            )}
          </div>

          <div className="su-profile-form-group">
            <label className="su-profile-label">Email Address</label>
            <div className="su-profile-value">
              {profile?.profile.email}
            </div>
            <p className="su-profile-hint">Email cannot be changed here</p>
          </div>

          <div className="su-profile-form-group">
            <label className="su-profile-label">Account Created</label>
            <div className="su-profile-value">
              {profile?.profile.created_at
                ? new Date(profile.profile.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "N/A"}
            </div>
          </div>

          <div className="su-profile-form-group">
            <label className="su-profile-label">Role</label>
            <div className="su-profile-value">
              {profile?.profile.onboarding?.role ||
                profile?.role?.type ||
                profile?.profile.role || (
                  <span className="su-profile-value-muted">Not specified</span>
                )}
            </div>
          </div>

          <div className="su-profile-form-group">
            <label className="su-profile-label">User ID</label>
            <div className="su-profile-value" style={{ fontSize: '0.875rem', opacity: 0.7, fontFamily: 'monospace' }}>
              {profile?.profile.id || (
                <span className="su-profile-value-muted">N/A</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Company Information Section */}
      <div className="su-section">
        <div className="su-section-header">
          <h2 className="su-text-section-heading">Company Information</h2>
        </div>
        <div className="su-profile-form-grid">
          <div className="su-profile-form-group">
            <label className="su-profile-label">Company Name</label>
            {isEditing ? (
              <input
                type="text"
                value={companyForm.name}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, name: e.target.value })
                }
                className="su-profile-input"
                placeholder="Enter company name"
              />
            ) : (
              <div className="su-profile-value">
                {companyForm.name || (
                  <span className="su-profile-value-muted">Not specified</span>
                )}
              </div>
            )}
          </div>

          <div className="su-profile-form-group">
            <label className="su-profile-label">Website URL</label>
            {isEditing ? (
              <input
                type="url"
                value={companyForm.website_url}
                onChange={(e) =>
                  setCompanyForm({
                    ...companyForm,
                    website_url: e.target.value,
                  })
                }
                className="su-profile-input"
                placeholder="https://example.com"
              />
            ) : (
              <div className="su-profile-value">
                {companyForm.website_url ? (
                  <a
                    href={companyForm.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:underline inline-flex items-center gap-1"
                  >
                    {companyForm.website_url}
                    <ExternalLinkIcon className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="su-profile-value-muted">Not specified</span>
                )}
              </div>
            )}
          </div>

          <div className="su-profile-form-group full-width">
            <label className="su-profile-label">Company Description</label>
            {isEditing ? (
              <textarea
                value={companyForm.description}
                onChange={(e) =>
                  setCompanyForm({
                    ...companyForm,
                    description: e.target.value,
                  })
                }
                className="su-profile-textarea"
                placeholder="Brief description of your company"
                rows={3}
              />
            ) : (
              <div className="su-profile-value">
                {companyForm.description || (
                  <span className="su-profile-value-muted">Not specified</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account Settings Redirect Section */}
      <div className="su-section">
        <div className="su-section-header-flat">
          <h2 className="su-text-section-heading">Account Settings</h2>
        </div>
        <p className="text-gray-400 mb-4">
          Manage your timezone, language, and notification preferences on the
          Settings page.
        </p>
        <button
          onClick={() => navigate("/settings")}
          className="su-btn-secondary"
        >
          GO TO SETTINGS
        </button>
      </div>

      {/* Connected Integrations Section */}
      <div className="su-section">
        <div className="su-section-header">
          <h2 className="su-text-section-heading">Connected Integrations</h2>
        </div>
        {integrations && integrations.length > 0 ? (
          <div className="su-profile-integration-list">
            {integrations.map((integration: UserIntegration) => {
              const iconSrc = getIntegrationIconSrc(integration.provider);
              const providerName = integration.provider.charAt(0).toUpperCase() + integration.provider.slice(1);
              
              return (
                <div key={integration.id} className="su-profile-integration-item">
                  <div className="su-profile-integration-info">
                    <div className="su-profile-integration-icon">
                      {iconSrc ? (
                        <img
                          src={iconSrc}
                          alt={providerName}
                          style={{ width: "20px", height: "20px", objectFit: "contain" }}
                        />
                      ) : (
                        <span>
                          {integration.provider.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="su-profile-integration-details">
                      <span className="su-profile-integration-name">
                        {providerName}
                      </span>
                      <span className="su-profile-integration-date">
                        Connected on{" "}
                        {new Date(integration.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`su-badge-${
                      integration.integration_status === "active"
                        ? "active"
                        : "paused"
                    }`}
                  >
                    {integration.integration_status.toUpperCase()}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="su-profile-empty">
            <p>No integrations connected</p>
            <button
              onClick={() => navigate("/integrations")}
              className="su-btn-primary"
            >
              CONNECT INTEGRATIONS
            </button>
          </div>
        )}
      </div>

      {/* Subscription Section */}
      <div className="su-section">
        <div className="su-section-header">
          <h2 className="su-text-section-heading">Subscription</h2>
        </div>

        {subscriptionLoading ? (
          <div className="su-profile-value">Loading subscription...</div>
        ) : (
          <>
            <div className="su-profile-subscription-grid">
              <div className="su-profile-form-group">
                <label className="su-profile-label">Current Plan</label>
                <div className="su-profile-value" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "1.125rem", fontWeight: "600" }}>
                    {PLAN_DISPLAY_NAMES[features.planTier] || features.planTier}
                  </span>
                  {features.planTier !== "free" && (
                    <span className="su-badge-active">ACTIVE</span>
                  )}
                </div>
              </div>
              <div className="su-profile-form-group">
                <label className="su-profile-label">Status</label>
                <div style={{ marginTop: "8px" }}>
                  <span
                    className={`su-badge-${
                      features.planTier !== "free" ? "active" : "paused"
                    }`}
                  >
                    {features.planTier === "free" ? "FREE TIER" : "ACTIVE"}
                  </span>
                </div>
              </div>
            </div>

            {/* Feature Access */}
            <div style={{ marginTop: "20px" }}>
              <label className="su-profile-label" style={{ marginBottom: "12px", display: "block" }}>
                Feature Access
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                {/* Sends */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: "#111",
                  border: "1px solid #333",
                }}>
                  <CheckIcon style={{ color: "#10B981", width: "16px", height: "16px" }} />
                  <span style={{ color: "#D1D5DB" }}>
                    {features.unlimitedSends
                      ? "Unlimited Sends"
                      : `${features.weeklySendsRemaining ?? features.sendsPerWeek} sends remaining this week`}
                  </span>
                </div>

                {/* Automations */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: "#111",
                  border: "1px solid #333",
                }}>
                  {features.canUseAutomations ? (
                    <CheckIcon style={{ color: "#10B981", width: "16px", height: "16px" }} />
                  ) : (
                    <XIcon style={{ color: "#6B7280", width: "16px", height: "16px" }} />
                  )}
                  <span style={{ color: features.canUseAutomations ? "#D1D5DB" : "#6B7280" }}>
                    Automations
                  </span>
                </div>

                {/* Data Room */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: "#111",
                  border: "1px solid #333",
                }}>
                  {features.canUseDataRoom ? (
                    <CheckIcon style={{ color: "#10B981", width: "16px", height: "16px" }} />
                  ) : (
                    <XIcon style={{ color: "#6B7280", width: "16px", height: "16px" }} />
                  )}
                  <span style={{ color: features.canUseDataRoom ? "#D1D5DB" : "#6B7280" }}>
                    Data Room
                  </span>
                </div>

                {/* Deck Storage */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px",
                  backgroundColor: "#111",
                  border: "1px solid #333",
                }}>
                  {features.canUseDecks ? (
                    <CheckIcon style={{ color: "#10B981", width: "16px", height: "16px" }} />
                  ) : (
                    <XIcon style={{ color: "#6B7280", width: "16px", height: "16px" }} />
                  )}
                  <span style={{ color: features.canUseDecks ? "#D1D5DB" : "#6B7280" }}>
                    Deck Storage
                  </span>
                </div>
              </div>
            </div>

            {/* Promo Status Card */}
            {promoStatus?.active && (
              <div style={{
                marginTop: "20px",
                padding: "16px",
                backgroundColor: "#0f172a",
                border: "1px solid #6366f1",
                borderRadius: "4px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ color: "#818cf8", fontWeight: 600, fontSize: "0.875rem" }}>
                      PROMO: FREE {(promoStatus.plan_tier || "pro").toUpperCase()} ACCESS
                    </span>
                    <div style={{ color: "#D1D5DB", marginTop: "4px", fontSize: "0.875rem" }}>
                      Expires {promoStatus.expires_at ? new Date(promoStatus.expires_at).toLocaleDateString() : "N/A"} ({promoStatus.days_remaining} days remaining)
                    </div>
                  </div>
                  <span className="su-badge-active">ACTIVE</span>
                </div>
              </div>
            )}

            {/* Upgrade/Manage Buttons */}
            <div style={{ marginTop: "20px", display: "flex", gap: "12px" }}>
              {promoStatus?.active && features.planTier !== "free" ? (
                <button
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="su-btn-primary"
                >
                  SUBSCRIBE TO PRO
                </button>
              ) : features.planTier === "free" ? (
                <button
                  onClick={() => setIsUpgradeModalOpen(true)}
                  className="su-btn-primary"
                >
                  UPGRADE PLAN
                </button>
              ) : (
                <>
                  {features.planTier === "basic" && (
                    <button
                      onClick={() => setIsUpgradeModalOpen(true)}
                      className="su-btn-primary"
                    >
                      UPGRADE TO PRO
                    </button>
                  )}
                  <button
                    onClick={handleManageSubscription}
                    className="su-btn-secondary"
                    disabled={managingSubscription}
                  >
                    {managingSubscription ? "OPENING..." : "MANAGE SUBSCRIPTION"}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />

      {/* Account Actions Section */}
      <div className="su-section">
        <div className="su-section-header">
          <h2 className="su-text-section-heading">Account Actions</h2>
        </div>
        <div className="su-profile-actions">
          <button onClick={handleLogout} className="su-btn-secondary">
            SIGN OUT
          </button>
          <div className="su-profile-danger-zone">
            <p>
              Need to delete your account? This action cannot be undone.
            </p>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to delete your account? This action cannot be undone."
                  )
                ) {
                  showError(
                    "Not Implemented",
                    "Account deletion is not implemented yet. Please contact support."
                  );
                }
              }}
              className="su-btn-danger"
            >
              DELETE ACCOUNT
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Payment Success Modal */}
    <PaymentSuccessModal
      isOpen={showPaymentSuccessModal}
      onClose={closePaymentSuccessModal}
      planTier={paymentPlanTier}
    />
    </>
  );
};

export default Profile;
